import React, { useCallback, useEffect, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { ui } from '../../ui';
import { Category, IntradayVolumeResponse, getInsightsIntraday } from '../../api';
import { LoadingSpinner } from '../../../../components/common/LoadingSpinner';
import { CategoryMultiSelect } from './CategoryMultiSelect';
import { etToday, POLL_MS } from '../../dateRange';

interface IntradayVolumeChartProps {
  from: string;
  to: string;
  categories: Category[];
}

const axisTick = { fill: '#94a3b8', fontSize: 12 };

// "14:30" -> "2:30p" (compact 12-hour, matches the existing Metrics-tab hour axis convention).
const fmtSlot = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${h < 12 ? 'a' : 'p'}` : `${h12}:${String(m).padStart(2, '0')}${h < 12 ? 'a' : 'p'}`;
};

const rangeIncludesToday = (from: string, to: string) => {
  const today = etToday();
  return from <= today && today <= to;
};

// FR Group 1 — intraday call volume in 96 fifteen-minute ET buckets, stacked answered/missed,
// filterable by category (OR logic), summed across every day in the range. Only this Insights
// report auto-refreshes (OQ-9), and only while the selected range includes ET "today".
export const IntradayVolumeChart: React.FC<IntradayVolumeChartProps> = ({ from, to, categories }) => {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [data, setData] = useState<IntradayVolumeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((background: boolean) => {
    if (background) setRefreshing(true); else setLoading(true);
    setError(null);
    return getInsightsIntraday(from, to, selectedCategoryIds)
      .then(setData)
      .catch(() => setError('Could not load intraday call volume — try again.'))
      .finally(() => { if (background) setRefreshing(false); else setLoading(false); });
  }, [from, to, selectedCategoryIds]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    if (!rangeIncludesToday(from, to)) return;
    const id = setInterval(() => { load(true); }, POLL_MS);
    return () => clearInterval(id);
  }, [from, to, load]);

  const buckets = data?.Buckets ?? [];
  const chartData = buckets.map(b => ({
    interval: b.IntervalStartET,
    answered: b.Calls,
    missed: b.Missed,
  }));
  // Calls is the answered count, not a total (see IntradayVolumeBucket) — a range with only
  // missed calls (0 answered) must still render the chart, not the "No calls in range." state.
  const totalCalls = buckets.reduce((sum, b) => sum + b.Calls + b.Missed, 0);
  const hourlyTicks = buckets.filter(b => b.IntervalStartET.endsWith(':00')).map(b => b.IntervalStartET);

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-secondary">Intraday Call Volume</h3>
      <div className="mb-4 flex flex-wrap items-end gap-6">
        <CategoryMultiSelect
          idPrefix="intraday"
          categories={categories}
          selected={selectedCategoryIds}
          onChange={setSelectedCategoryIds}
        />
        <button className={ui.btnSecondary} onClick={() => load(false)} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div aria-live="polite" role="status" className="sr-only">
        {loading ? 'Loading intraday call volume…' : refreshing ? 'Refreshing intraday call volume…' : ''}
      </div>

      {loading && <LoadingSpinner size="sm" message="Loading intraday call volume…" />}
      {!loading && error && (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}
      {!loading && !error && (
        <>
          <div style={{ height: 340 }}>
            {totalCalls === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted">No calls in range.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis
                    dataKey="interval"
                    ticks={hourlyTicks}
                    tickFormatter={fmtSlot}
                    tick={axisTick}
                    tickLine={false}
                    axisLine={{ stroke: '#334155' }}
                  />
                  <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={{ stroke: '#334155' }} />
                  <Tooltip
                    cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                    labelFormatter={(v) => `${fmtSlot(String(v))} ET`}
                  />
                  <Legend />
                  <Bar dataKey="answered" name="Answered" stackId="calls" fill="#16a34a" />
                  <Bar dataKey="missed" name="Missed" stackId="calls" fill="#dc2626" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <p className="mt-2 text-xs text-muted">
            Bucketed by inbound-event time (ET), including missed calls.
            {data && data.Days > 1 && <> Summed across {data.Days} days.</>}
            {selectedCategoryIds.length > 0 && (
              <> A call with multiple selected categories counts once per matching category, so
                totals here may exceed the overall call count.</>
            )}
          </p>
        </>
      )}
    </div>
  );
};
